import React, { useState, useEffect } from 'react';
import { User } from 'lucide-react';
import { submitCashTip } from '../services/cashTipService';
import { getStaff, Staff } from '../services/staffService';
import { getPlatformConfig } from '../services/platformConfigService';
import { useFirebase } from '../contexts/FirebaseContext';
import { getLocalDate } from '../lib/utils';
import { getSettings } from '../services/settingsService';
import { getIRSCashTipDeadline, formatLongDate } from '../lib/complianceUtils';
import { useStoreLocation } from '../contexts/LocationContext';

interface Props {
  tenantId: string;
  isAdminOrManager: boolean;
  parentTxId?: string;
}

export const CashTipForm: React.FC<Props> = ({ tenantId, isAdminOrManager, parentTxId }) => {
  const { user } = useFirebase();
  const { activeLocationId, activeLocation } = useStoreLocation();
  const [formData, setFormData] = useState({
    staffName: '',
    email: user?.email || '',
    tipDate: '', // Initialize empty, will be set in useEffect
    shift: '',
    amount: '',
    notes: '',
    paidBy: 'Paycheck',
    paymentDate: ''
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [payoutMethods, setPayoutMethods] = useState<string[]>(['Paycheck', 'Cash', 'Zelle', 'PayPal/Venmo', 'Other']);
  const [shiftList, setShiftList] = useState<any[]>([]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const settingsData = await getSettings(tenantId);
      setSettings(settingsData);
      const timezone = settingsData?.timezone || 'UTC';
      const today = getLocalDate(timezone);
      
      const [staff, payoutConfig, shifts] = await Promise.all([
        getStaff(tenantId),
        getPlatformConfig('payoutMethods'),
        import('../services/shiftService').then(m => m.getShifts(tenantId))
      ]);

      if (payoutConfig) {
        setPayoutMethods(payoutConfig.options);
      }
      
      setShiftList(shifts);
      
      const currentUserStaff = staff.find(s => s.email.toLowerCase() === user?.email?.toLowerCase());
      
      const filteredStaff = isAdminOrManager ? staff : (currentUserStaff ? [currentUserStaff] : staff.filter(s => s.email.toLowerCase() === user?.email?.toLowerCase()));
      
      setStaffList(filteredStaff);
      
      setFormData(prev => ({ 
        ...prev, 
        tipDate: today,
        staffName: currentUserStaff?.name || user?.displayName || prev.staffName, 
        email: user?.email || prev.email,
        paidBy: settingsData?.payoutMethod || 'Paycheck'
      }));
      setLoading(false);
    };
    init();
  }, [tenantId, user, isAdminOrManager]);

  if (loading) {
    return (
      <div className="card p-6 flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
        <p className="text-gray-500 font-medium">Loading form...</p>
      </div>
    );
  }

  const getComplianceInfo = () => {
    if (!formData.tipDate) return null;
    const parts = formData.tipDate.split('-');
    if (parts.length !== 3) return null;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-indexed for JS Date
    
    const deadlineDate = getIRSCashTipDeadline(year, month);
    const today = new Date();
    
    // Strip time from today for comparison
    const todayReset = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const isPastDeadline = todayReset > deadlineDate;
    
    const monthName = new Date(year, month, 1).toLocaleDateString('en-US', { month: 'long' });
    
    return {
      monthName,
      year,
      deadlineStr: formatLongDate(deadlineDate),
      isPastDeadline
    };
  };

  const compInfo = getComplianceInfo();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setStatus('Error: User not logged in. Please refresh the page.');
      return;
    }

    try {
      const numericAmount = parseFloat(formData.amount as string) || 0;
      
      let selectedStaff;
      
      // Try finding by name or email (for admins/managers)
      if (isAdminOrManager) {
        selectedStaff = staffList.find(s => 
          s.name.toLowerCase() === formData.staffName.toLowerCase() || 
          s.email.toLowerCase() === formData.email.toLowerCase()
        );
      } else {
        // Try finding by email
        selectedStaff = staffList.find(s => s.email.toLowerCase() === (formData.email || user?.email || '').toLowerCase());
      }

      if (!selectedStaff) {
        setStatus('Error: Please select a valid staff member.');
        return;
      }
      
      const staffId = selectedStaff.id;
      const staffName = selectedStaff.name;
      
      const tipPayload = {
        tenantId,
        staffId: staffId,
        staffName: staffName,
        email: selectedStaff.email || formData.email || user?.email || 'unknown',
        tipDate: formData.tipDate,
        shiftName: formData.shift, // Map shift to shiftName
        amount: numericAmount,
        notes: formData.notes,
        status: 'Pending' as const,
        paymentStatus: formData.paidBy === 'Paycheck' ? 'Unpaid' as const : 'Paid' as const,
        payoutMethod: formData.paidBy,
        submittedBy: user?.email?.toLowerCase() || 'Unknown',
        source: 'Cash' as const,
        masterTxId: 'unknown',
        parentTxId,
        posId: selectedStaff.posId || null,
        locationId: activeLocationId && activeLocationId !== 'all' ? activeLocationId : (selectedStaff.locationId || 'loc-downtown'),
        locationName: activeLocation?.name || selectedStaff.locationName || 'Downtown Bistro'
      };

      console.log('DEBUG [CashTipForm] numericAmount:', numericAmount);
      await submitCashTip(tenantId, tipPayload);
      
      console.log('DEBUG [submitCashTip] Received tip:', { tenantId, tip: tipPayload });
  const settings = await getSettings(tenantId);
      const timezone = settings?.timezone || 'UTC';
      const today = getLocalDate(timezone);

      setStatus('Tip submitted successfully!');
      setFormData({
        staffName: '',
        email: '',
        tipDate: today,
        shift: '',
        amount: '',
        notes: '',
        paidBy: settings?.payoutMethod || 'Paycheck',
        paymentDate: ''
      });
    } catch (error) {
      console.error('DEBUG [CashTipForm] Error submitting tip:', error);
      setStatus('Error submitting tip.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card p-6 md:p-8 max-w-2xl mx-auto space-y-8">
      <div className="space-y-8">
        {/* User identification */}
        <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex items-center gap-4">
          <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-gray-400 shadow-sm shrink-0">
            <User size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Submitted By (Staff Profile)
            </label>
            {isAdminOrManager ? (
              <div className="mt-1">
                <select
                  value={formData.email}
                  onChange={e => {
                    const selected = staffList.find(s => s.email.toLowerCase() === e.target.value.toLowerCase());
                    setFormData({
                      ...formData,
                      email: e.target.value,
                      staffName: selected?.name || formData.staffName
                    });
                  }}
                  className="input-field h-11 text-sm font-bold w-full bg-white"
                >
                  <option value="">Select Staff Member...</option>
                  {staffList.map(s => (
                    <option key={s.id || s.email} value={s.email}>
                      {s.name} {s.posId ? `[POS ID: ${s.posId}]` : ''} {s.id ? `[Staff ID: ${s.id.slice(0, 8)}]` : ''}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex flex-col">
                <p className="text-sm font-bold text-gray-900">{formData.staffName || user?.displayName || 'Set Name'}</p>
                <span className="text-xs text-gray-400 font-medium">{formData.email}</span>
              </div>
            )}

            {/* Resolved staff identification badges */}
            {(() => {
              const matched = staffList.find(s => s.email.toLowerCase() === formData.email.toLowerCase() || s.name.toLowerCase() === formData.staffName.toLowerCase());
              return matched ? (
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {matched.posId && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-mono font-bold uppercase tracking-wider">
                      POS ID: {matched.posId}
                    </span>
                  )}
                  {matched.id && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-200 text-slate-800 text-[10px] font-mono font-bold tracking-wider" title={`Full Staff ID: ${matched.id}`}>
                      Staff ID: {matched.id.slice(0, 8)}
                    </span>
                  )}
                  {matched.role && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider">
                      Role: {matched.role}
                    </span>
                  )}
                </div>
              ) : null;
            })()}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <label className="block text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Service Date</label>
            <input 
              type="date" 
              value={formData.tipDate} 
              onChange={e => setFormData({...formData, tipDate: e.target.value})} 
              className="input-field h-14 text-base font-bold" 
              required 
            />
          </div>
          <div className="space-y-4">
            <label className="block text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Service Shift</label>
            <select 
              value={formData.shift} 
              onChange={e => setFormData({...formData, shift: e.target.value})} 
              className="input-field h-14 text-base font-bold"
              required
            >
              <option value="">Select Shift...</option>
              {shiftList.map((s, index) => (
                <option key={s.id || index} value={s.shiftName}>{s.shiftName}</option>
              ))}
            </select>
          </div>
        </div>

        {compInfo && (
          <div className={`p-4 rounded-xl border flex gap-3 ${
            compInfo.isPastDeadline 
              ? 'bg-amber-50 border-amber-100 text-amber-900' 
              : 'bg-emerald-50 border-emerald-100 text-emerald-900'
          }`}>
            <div className="shrink-0 text-lg mt-0.5">
              {compInfo.isPastDeadline ? '⚠️' : '📋'}
            </div>
            <div className="text-xs">
              <p className="font-bold uppercase tracking-wider text-[10px] opacity-80">
                IRS Compliance Tracking ({compInfo.monthName} {compInfo.year})
              </p>
              <p className="mt-1 font-medium">
                Cash tips received in {compInfo.monthName} must be reported on or before <span className="font-bold underline">{compInfo.deadlineStr}</span>.
              </p>
              {compInfo.isPastDeadline ? (
                <p className="mt-1 text-amber-700 font-medium">
                  Note: This service date is past the IRS reporting deadline. Please consult management or a tax professional regarding late tip reports.
                </p>
              ) : (
                <p className="mt-1 text-emerald-700 font-medium">
                  In Compliance: Within the standard IRS tip reporting period.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-4">
          <label className="block text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Amount ($)</label>
          <input 
            type="number" 
            step="0.01"
            placeholder="0.00"
            value={formData.amount} 
            onChange={e => setFormData({...formData, amount: e.target.value})} 
            onWheel={e => e.currentTarget.blur()}
            className="input-field h-14 text-lg font-black" 
            required 
          />
        </div>

          {settings?.allowPayoutOverride ? (
            <div className="space-y-4">
              <label className="block text-xs font-black text-gray-400 uppercase tracking-[0.2em]">Payout Method</label>
              <select
                value={formData.paidBy}
                onChange={e => setFormData({...formData, paidBy: e.target.value})}
                className="input-field h-14 text-base font-bold shadow-sm"
              >
                {payoutMethods.map(method => <option key={method} value={method}>{method}</option>)}
              </select>
            </div>
          ) : (
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-100 flex justify-between items-center">
              <div>
                <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Payout Method (Company Default)</p>
                <p className="text-sm font-bold text-blue-700">{formData.paidBy === 'Paycheck' ? 'Add to next paycheck' : `Already paid - ${formData.paidBy}`}</p>
              </div>
              <div className="text-blue-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
            </div>
          )}

        <div className="space-y-2">
          <label className="block text-sm font-semibold text-gray-700">Notes (Optional)</label>
          <textarea 
            placeholder="Add any additional details..." 
            value={formData.notes} 
            onChange={e => setFormData({...formData, notes: e.target.value})} 
            className="input-field min-h-[100px] py-3" 
          />
        </div>

        <div className="p-4 bg-amber-50 rounded-xl border border-amber-100 flex gap-3 text-amber-900">
          <span className="text-base leading-none">⚠️</span>
          <div className="text-xs leading-normal">
            <span className="font-bold">IRS Cash Tip 10-Day Reminder:</span> Under IRS guidelines, if you receive $20 or more in cash tips during any single calendar month, they must be reported to your employer by the 10th day of the following month.
          </div>
        </div>

        <button type="submit" className="w-full h-20 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-100 disabled:text-gray-400 text-white rounded-[2rem] text-xl font-black uppercase italic tracking-widest flex items-center justify-center gap-4 shadow-2xl shadow-blue-600/30 transition-all active:scale-[0.98] tap-highlight-transparent">
          Submit Cash Tip
        </button>
      </div>

      {status && (
        <div className={`mt-6 p-4 rounded-xl text-center font-medium ${status.includes('Error') ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-green-50 text-green-700 border border-green-100'}`}>
          {status}
        </div>
      )}
    </form>
  );
};
